import { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import constants from "../../constants";
import { useGetPatientChatAccountOwnerList } from "repositories/family-member-repository";
import { useQueryClient } from "react-query";
import { getStorage } from "utils";
import { useAccountOwnerCommon } from "lib/Context/AccountOwner";

/**
 * Subscription Redux starts as `{}` (truthy) before plan features arrive.
 * Chat must wait for real plan data before treating team/patient chat as
 * disabled — account owners have `selectedOwner` immediately and would
 * otherwise bounce to `/` (office listing).
 *
 * @param {object|Array|null|undefined} subscriptionDetails
 * @returns {boolean}
 */
function hasResolvedSubscription(subscriptionDetails) {
  if (!subscriptionDetails) {
    return false;
  }
  if (subscriptionDetails.planFeature) {
    return true;
  }
  return Array.isArray(subscriptionDetails) && subscriptionDetails.length > 0;
}

const useSelectedAccountOwner = ({
  localStorageKey,
  setCurrentChannel,
  isPatient = false,
  doctorChatCheck = false,
}) => {
  const cachedSelectedDashBoardTab = getStorage(
    constants.notes.cache.dashboardAccountOwnerCheck
  );
  const {
    ownerList: ownerData,
    commonOwnerData: selectedOwner,
    isOwner: isAccountOwner,
    switchNewAccountOwner: switchNewAccountOwnerCommon,
    subscriptionValues: {
      isPatientConversationFeatureEnabled,
      isTeamConversationFeatureEnabled,
    },
    subscriptionDetails,
    isStaff,
    ownerListLoading: loadinOwnerData,
  } = useAccountOwnerCommon();
  const profile = useSelector((state) => state.userProfile.profile);
  const queryClient = useQueryClient();
  const history = useHistory();
  const [showSwitchOwnerModal, setShowSwitchOwnerModal] = useState(false);

  const { isLoading: loadinOwnerDataForPatient, data: ownerDataForPatient } =
    useGetPatientChatAccountOwnerList({
      enabled:
        !!isPatient &&
        profile.role.systemRole !== constants.systemRoles.accountOwner &&
        !doctorChatCheck,
    });

  const list = isPatient ? ownerDataForPatient : ownerData;

  useEffect(() => {
    if (!isStaff) {
      localStorage.removeItem(localStorageKey);
    }
  }, [isStaff, localStorageKey]);

  const shouldRedirectToHome = useMemo(
    () =>
      hasResolvedSubscription(subscriptionDetails) &&
      selectedOwner &&
      ((isPatient && !isPatientConversationFeatureEnabled) ||
        (!isPatient && !isTeamConversationFeatureEnabled)) &&
      ((isStaff && ownerData?.length === 1) || isAccountOwner),
    [
      subscriptionDetails,
      selectedOwner,
      isPatient,
      isPatientConversationFeatureEnabled,
      isTeamConversationFeatureEnabled,
      isStaff,
      ownerData,
      isAccountOwner,
    ],
  );

  const isAccessible =
    subscriptionDetails &&
    selectedOwner &&
    ((isPatient && !isPatientConversationFeatureEnabled) ||
      (!isPatient && !isTeamConversationFeatureEnabled)) &&
    ((isStaff && ownerData?.length === 1) || isAccountOwner);

  useEffect(() => {
    if (shouldRedirectToHome) {
      history.push("/");
    }
    return () => {
      queryClient.invalidateQueries([
        "/Doctor/DoctorOfficesUnderOwner",
        "/Doctor/OwnerListByPatientChatPreference",
      ]);
    };
  }, [shouldRedirectToHome, queryClient, list, history]);

  const switchNewAccountOwner = useCallback(
    (owner) => {
      if (!owner) return;
      switchNewAccountOwnerCommon(owner);
      setCurrentChannel(null);
    },
    [setCurrentChannel, switchNewAccountOwnerCommon],
  );

  return {
    state: {
      showSwitchOwnerModal,
      selectedOwner,
      ownerData,
      loadinOwnerData: loadinOwnerData || loadinOwnerDataForPatient,
      isAccessible: isAccessible,
      cachedSelectedDashBoardTab,
      isAccountOwner,
      isStaff,
    },
    updateMethods: {
      setSelectedOwner: switchNewAccountOwnerCommon,
      setShowSwitchOwnerModal,
    },
    otherMethods: {
      switchNewAccountOwner,
    },
  };
};

export default useSelectedAccountOwner;
